import React, { useState } from 'react';
import { CheckCircle2Icon, KeyRoundIcon, UserRoundIcon } from 'lucide-react';
import { Field } from './Field';
import { useScreenInit } from '../useScreenInit';
export type AccountProfile = {
  firstName: string;
  lastName: string;
  email: string;
};
type AccountPanelProps = {
  profile: AccountProfile;
  onProfileSave: (profile: AccountProfile) => void;
};
type AccountTab = 'profile' | 'password';
type PasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};
const accountTabs: {
  id: AccountTab;
  label: string;
}[] = [{
  id: 'profile',
  label: 'Profile details'
}, {
  id: 'password',
  label: 'Password'
}];
const emptyPasswordForm: PasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};
export function AccountPanel({
  profile,
  onProfileSave
}: AccountPanelProps) {
  const screenInit = useScreenInit() as {
    tab?: AccountTab;
  };
  const [activeTab, setActiveTab] = useState<AccountTab>(() => isAccountTab(screenInit.tab) ? screenInit.tab : 'profile');
  const [profileDraft, setProfileDraft] = useState<AccountProfile>(profile);
  const [profileErrors, setProfileErrors] = useState<Partial<Record<keyof AccountProfile, string>>>({});
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordDraft, setPasswordDraft] = useState<PasswordForm>(emptyPasswordForm);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, currentTab: AccountTab) {
    const currentIndex = accountTabs.findIndex(tab => tab.id === currentTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % accountTabs.length;
    }
    if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + accountTabs.length) % accountTabs.length;
    }
    if (event.key === 'Home') {
      nextIndex = 0;
    }
    if (event.key === 'End') {
      nextIndex = accountTabs.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = accountTabs[nextIndex];
    setActiveTab(nextTab.id);
    document.getElementById(`account-tab-${nextTab.id}`)?.focus();
  }
  function updateProfile(field: keyof AccountProfile, value: string) {
    setProfileDraft(current => ({
      ...current,
      [field]: value
    }));
    setProfileErrors(current => ({
      ...current,
      [field]: undefined
    }));
    setProfileMessage('');
  }
  function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextProfile = {
      firstName: profileDraft.firstName.trim(),
      lastName: profileDraft.lastName.trim(),
      email: profileDraft.email.trim().toLowerCase()
    };
    const errors: Partial<Record<keyof AccountProfile, string>> = {};
    if (!nextProfile.firstName) errors.firstName = 'Enter your first name.';
    if (!nextProfile.lastName) errors.lastName = 'Enter your last name.';
    if (!nextProfile.email) {
      errors.email = 'Enter your email address.';
    } else if (!isValidEmail(nextProfile.email)) {
      errors.email = 'Enter a valid email address.';
    }
    if (Object.keys(errors).length) {
      setProfileErrors(errors);
      return;
    }
    setProfileErrors({});
    setProfileDraft(nextProfile);
    onProfileSave(nextProfile);
    setProfileMessage('Your account details have been saved.');
  }
  function updatePassword(field: keyof PasswordForm, value: string) {
    setPasswordDraft(current => ({
      ...current,
      [field]: value
    }));
    setPasswordError('');
    setPasswordMessage('');
  }
  function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!passwordDraft.currentPassword || !passwordDraft.newPassword || !passwordDraft.confirmPassword) {
      setPasswordError('Complete all password fields to continue.');
      return;
    }
    if (passwordDraft.newPassword.length < 8) {
      setPasswordError('Your new password must be at least 8 characters.');
      return;
    }
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setPasswordError('Your new password and confirmation do not match.');
      return;
    }
    setPasswordDraft(emptyPasswordForm);
    setPasswordMessage('Your password has been updated locally.');
  }
  return <section aria-labelledby="account-title" data-id="element-20" data-mp="38qzempj6mmdt">
      <div className="border-b border-[#A6B6F8]/50 pb-6" data-id="element-21" data-mp="3dpsjjtkel52d">
        <h1 id="account-title" className="text-3xl font-bold tracking-tight text-[#041F60]" data-id="element-22" data-mp="3drdcxfuzehyn">
          Account
        </h1>
        <p className="mt-2 text-sm text-stone-600" data-id="element-23" data-mp="33yphaofqmk1i">
          Manage your personal details and sign-in settings.
        </p>
      </div>

      <div className="mt-5 max-w-3xl border-b border-[#A6B6F8]/50" data-id="element-24" data-mp="3rnqx4wqwgxgt">
        <div role="tablist" aria-label="Account settings" className="flex gap-5 overflow-x-auto" data-id="element-25" data-mp="bfpa1lmto41">
          {accountTabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} id={`account-tab-${tab.id}`} type="button" role="tab" tabIndex={isActive ? 0 : -1} aria-selected={isActive} aria-controls={`account-panel-${tab.id}`} onClick={() => setActiveTab(tab.id)} onKeyDown={event => handleTabKeyDown(event, tab.id)} className={`shrink-0 border-b-2 px-1 pb-3 text-sm font-bold outline-none transition ${isActive ? 'border-[#041F60] text-[#041F60]' : 'border-transparent text-stone-500 hover:border-[#A6B6F8] hover:text-[#041F60]'} focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2`} data-id="element-26" data-mp="1fzvqav76778r">
                {tab.label}
              </button>;
        })}
        </div>
      </div>

      {activeTab === 'profile' && <section id="account-panel-profile" role="tabpanel" aria-labelledby="account-tab-profile" className="mt-5 max-w-3xl rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-27" data-mp="1f4hyhocjfq4x">
          <div className="flex items-start gap-3 border-b border-[#A6B6F8]/50 pb-5" data-id="element-28" data-mp="1o44yq6tj6pwe">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#4BE4FF] text-[#041F60]" data-id="element-29" data-mp="1t2yde2kk0my5">
              <UserRoundIcon size={19} aria-hidden="true" data-id="element-30" data-mp="wcyupfaow81u" />
            </span>
            <div data-id="element-31" data-mp="10gn0bsfrgtkg">
              <h2 className="text-base font-bold" data-id="element-32" data-mp="10e0tjv0p2i3a">Profile details</h2>
              <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-33" data-mp="15dx1u6gfcvon">
                These details identify you in this workspace.
              </p>
            </div>
          </div>

          <form className="mt-5 space-y-4" onSubmit={saveProfile} noValidate data-id="element-34" data-mp="cprh3jpdf6ot">
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-35" data-mp="hpdovdmfjuq8">
              <Field id="account-first-name" label="First name" required value={profileDraft.firstName} error={profileErrors.firstName} onChange={value => updateProfile('firstName', value)} data-id="element-36" data-mp="hnsupp412dsw" />
              <Field id="account-last-name" label="Last name" required value={profileDraft.lastName} error={profileErrors.lastName} onChange={value => updateProfile('lastName', value)} data-id="element-37" data-mp="a1h8pm70hji" />
            </div>

            <Field id="account-email" label="Email address" required type="email" value={profileDraft.email} error={profileErrors.email} onChange={value => updateProfile('email', value)} data-id="element-38" data-mp="3dn38wl398wsb" />

            {profileMessage && <p role="status" className="flex items-center gap-2 rounded-lg border border-[#A6B6F8]/60 bg-[#A6B6F8]/15 px-3 py-2.5 text-sm font-medium text-[#5B5299]" data-id="element-39" data-mp="2zod9gysyql0s">
                <CheckCircle2Icon size={17} aria-hidden="true" data-id="element-40" data-mp="wd6z3aw18ov3" />
                {profileMessage}
              </p>}

            <button type="submit" className="inline-flex w-full items-center justify-center rounded-lg bg-[#041F60] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#031849] focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 sm:w-auto" data-id="element-41" data-mp="iga7h783b3d5">
              Save profile
            </button>
          </form>
        </section>}

      {activeTab === 'password' && <section id="account-panel-password" role="tabpanel" aria-labelledby="account-tab-password" className="mt-5 max-w-3xl rounded-xl border border-[#A6B6F8]/50 bg-white p-5 sm:p-6" data-id="element-42" data-mp="3o9u29gu0oka">
          <div className="flex items-start gap-3 border-b border-[#A6B6F8]/50 pb-5" data-id="element-43" data-mp="3qsxzo91m9gzj">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#A6B6F8]/20 text-[#5B5299]" data-id="element-44" data-mp="378hbrn5xhztp">
              <KeyRoundIcon size={19} aria-hidden="true" data-id="element-45" data-mp="32apbo5v4r7ef" />
            </span>
            <div data-id="element-46" data-mp="3g9f4vaetwmxi">
              <h2 className="text-base font-bold" data-id="element-47" data-mp="3g9ypiyvv5pf8">Change password</h2>
              <p className="mt-1 text-sm leading-5 text-stone-600" data-id="element-48" data-mp="3c5z90gal2qlv">
                Choose a new password with at least 8 characters.
              </p>
            </div>
          </div>

          <form className="mt-5 space-y-4" onSubmit={savePassword} noValidate data-id="element-49" data-mp="3l8icn2m71796">
            <Field id="account-current-password" label="Current password" required type="password" value={passwordDraft.currentPassword} onChange={value => updatePassword('currentPassword', value)} data-id="element-50" data-mp="3qz35dic0os8" />
            <Field id="account-new-password" label="New password" required type="password" value={passwordDraft.newPassword} onChange={value => updatePassword('newPassword', value)} data-id="element-51" data-mp="7t3uu6p7zbc8" />
            <Field id="account-confirm-password" label="Confirm new password" required type="password" value={passwordDraft.confirmPassword} onChange={value => updatePassword('confirmPassword', value)} data-id="element-52" data-mp="2mrwi031fudi1" />

            {passwordError && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-id="element-53" data-mp="35jz59x8l4s19">
                {passwordError}
              </p>}

            {passwordMessage && <p role="status" className="flex items-center gap-2 rounded-lg border border-[#A6B6F8]/60 bg-[#A6B6F8]/15 px-3 py-2.5 text-sm font-medium text-[#5B5299]" data-id="element-54" data-mp="1p5qv72504b6i">
                <CheckCircle2Icon size={17} aria-hidden="true" data-id="element-55" data-mp="3vv0vynmssni7" />
                {passwordMessage}
              </p>}

            <button type="submit" className="inline-flex w-full items-center justify-center rounded-lg border border-[#041F60] bg-white px-4 py-2.5 text-sm font-bold text-[#041F60] transition hover:bg-[#A6B6F8]/15 focus:outline-none focus:ring-2 focus:ring-[#4BE4FF] focus:ring-offset-2 sm:w-auto" data-id="element-56" data-mp="4ohkd6950tiu">
              Update password
            </button>
          </form>
        </section>}
    </section>;
}
function isAccountTab(tab: unknown): tab is AccountTab {
  return tab === 'profile' || tab === 'password';
}
function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}